// AddPatientModal.js
import React, { useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, FormFeedback } from "reactstrap";
import axios from "axios";
import fitzpatrickScaleImage from '../skinColor.png';
import Select from 'react-select';
import { MEDICATIONS } from './ImportantMedicationModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const initialPatientData = {
    subjectName: "",
    birthYear: '',
    gender: "",
    height: "",
    heightFeet: '',
    heightInches: '',
    heightUnit: 'ft_in',
    weight: "",
    weightUnit: "lbs",
    bmi: "",
    childBearingPotential: null,
    childBearingPotentialReason: null,
    childBearingPotentialOther: null,
    race: "",
    otherRace: null,
    ethnicity: "",
    hasMedicalHistory: null,
    medicalHistory: [],
    hasConcomitantMedication: null,
    concomitantMedication: [],
    hasDiabetes: "",
    skinColor: "",
    isSmoker: "",
    cigarettesPerDay: null,
    alcoholConsumption: "",
    exerciseIntensity: "",
    hasThyroidCondition: "",
    hasHypertension: "",
    parentalHypertensionHistory: "",
    bodyTemperature: "",
    bodyTemperatureUnit: "F", // Default to Fahrenheit
    usesMarijuana: "",
    marijuanaFrequency: "",
};

const AddPatientModal = ({ isOpen, toggle, onPatientAdded }) => {
    const [patientData, setPatientData] = useState(initialPatientData);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [calculatedAge, setCalculatedAge] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            resetForm();
        }
    }, [isOpen]);

    useEffect(() => {
        calculateBMI();
    }, [patientData.height, patientData.weight, patientData.heightUnit, patientData.weightUnit]);

    useEffect(() => {
        calculateAge();
        if (touched.birthYear) validateField('birthYear');
    }, [patientData.birthYear]);


    const resetForm = () => {
        setPatientData(initialPatientData);
        setErrors({});
        setTouched({});
        setCalculatedAge(null);
        setIsSubmitting(false);
    };
    const getThirtyDaysAgo = () => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0]; // Returns in YYYY-MM-DD format
    };
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        let processedValue = value;

        if (type === 'number') {
            processedValue = value === '' ? '' : Number(value);
        } else if (value === '') {
            processedValue = null;
        }

        setPatientData(prevData => {
            const newData = { ...prevData, [name]: processedValue };

            if (name === 'heightUnit') {
                // Reset height fields when unit changes
                newData.height = '';
                newData.heightFeet = '';
                newData.heightInches = '';
            }

            // Recalculate total height in selected unit
            if (newData.heightUnit === 'ft_in' && (name === 'heightFeet' || name === 'heightInches')) {
                const feet = Number(newData.heightFeet) || 0;
                const inches = Number(newData.heightInches) || 0;
                newData.height = (feet * 12 + inches).toFixed(2);
            }
            if (name === 'birthYear') {
                const currentYear = new Date().getFullYear();
                const age = currentYear - processedValue;
                newData.calculatedAge = age >= 0 ? age : null;
            }

            if (name === 'gender') {
                newData.childBearingPotential = null;
                newData.childBearingPotentialOther = null;
                newData.childBearingPotentialReason = null;
            }
            if (name === 'weightUnit') {
                newData.weight = '';
            }

            // Clear concomitant medication data when changed to 'no'
            if (name === 'hasConcomitantMedication' && value === 'no') {
                newData.concomitantMedication = [];
            }

            // Clear medical history data when changed to 'no'
            if (name === 'hasMedicalHistory' && value === 'no') {
                newData.medicalHistory = [];
            }

            if (name === 'bodyTemperatureUnit') {
                newData.bodyTemperature = '';
            }


            // Recalculate height in inches when feet or inches change
            if (name === 'heightFeet' || name === 'heightInches' || name === 'heightUnit') {
                if (newData.heightUnit === 'ft_in') {
                    const feet = Number(newData.heightFeet) || 0;
                    const inches = Number(newData.heightInches) || 0;
                    newData.height = feet * 12 + inches;
                } else {
                    // If cm is selected, convert cm to inches
                    newData.height = newData.height / 2.54;
                }
            }

            if (name === 'usesMarijuana' && value === 'no') {
                newData.marijuanaFrequency = '';
            }


            return newData;
        });


        setTouched(prevTouched => ({
            ...prevTouched,
            [name]: true
        }));

        // Clear errors related to concomitant medication
        if (name === 'hasConcomitantMedication' && value === 'no') {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                Object.keys(newErrors).forEach(key => {
                    if (key.startsWith('concomitantMedication')) {
                        delete newErrors[key];
                    }
                });
                return newErrors;
            });
        }

        // Clear errors related to medical history
        if (name === 'hasMedicalHistory' && value === 'no') {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                Object.keys(newErrors).forEach(key => {
                    if (key.startsWith('medicalHistory')) {
                        delete newErrors[key];
                    }
                });
                return newErrors;
            });
        }

        validateField(name, processedValue);
    };

    const calculateBMI = () => {
        let heightInMeters, weightInKg;

        if (patientData.heightUnit === 'cm') {
            heightInMeters = Number(patientData.height) / 100;
        } else {
            // Height is already in inches from handleInputChange
            heightInMeters = Number(patientData.height) * 0.0254;
        }

        if (patientData.weightUnit === "kg") {
            weightInKg = Number(patientData.weight);
        } else {
            weightInKg = Number(patientData.weight) * 0.45359237; // Convert lbs to kg
        }

        if (heightInMeters > 0 && weightInKg > 0) {
            const bmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(1);
            setPatientData(prevData => ({ ...prevData, bmi: Number(bmi) }));
            validateField('bmi', Number(bmi));
        } else {
            setPatientData(prevData => ({ ...prevData, bmi: null }));
        }
    };
    const getTodayDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    const calculateAge = () => {
        if (patientData.birthYear) {
            const currentYear = new Date().getFullYear();
            let age = currentYear - patientData.birthYear
            setCalculatedAge(age);
        } else {
            setCalculatedAge(null);
        }
    };

    const handleAddProfile = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        const allTouched = Object.keys(patientData).reduce((acc, key) => {
            acc[key] = true;
            return acc;
        }, {});
        setTouched(allTouched);

        // Validate all fields

        Object.keys(patientData).forEach(key => validateField(key, patientData[key]));

        // Check if all required fields are filled and valid
        const requiredFields = Object.keys(patientData).filter(key =>
            key !== 'bmi' &&
            key !== 'childBearingPotential' &&
            key !== 'childBearingPotentialReason' &&
            key !== 'childBearingPotentialOther' &&
            key !== 'otherRace' &&
            key !== 'heightFeet' &&
            key !== 'heightInches' &&
            key !== 'cigarettesPerDay' &&
            key !== 'bodyTemperature' &&
            key !== 'marijuanaFrequency'
        );

        const formIsValid = requiredFields.every(key => (key === 'subjectName' ? patientData[key] && patientData[key].trim() !== '' : patientData[key] !== "" && patientData[key] !== null)) &&
            (patientData.gender !== 'female' || patientData.childBearingPotential !== null) &&
            (patientData.childBearingPotential !== "notAWomanOfChildBearingPotential" || patientData.childBearingPotentialReason !== null) &&
            (patientData.childBearingPotentialReason !== "other" || (patientData.childBearingPotentialOther && patientData.childBearingPotentialOther.trim() !== '')) &&
            (patientData.race !== "other" || patientData.otherRace !== null) &&
            patientData.hasMedicalHistory !== null &&
            patientData.hasConcomitantMedication !== null && (patientData.hasConcomitantMedication !== 'yes' ||
                patientData.concomitantMedication.every(med =>
                    med.ongoing || (med.endDate && new Date(med.endDate) >= new Date(getThirtyDaysAgo()))
                )) &&
            (patientData.isSmoker === 'no' || (patientData.cigarettesPerDay !== null && patientData.cigarettesPerDay >= 0)) &&
            patientData.alcoholConsumption !== "" &&
            patientData.exerciseIntensity !== "" &&
            patientData.hasThyroidCondition !== "" &&
            patientData.hasHypertension !== "" &&
            patientData.parentalHypertensionHistory !== "" &&
            patientData.usesMarijuana !== "" &&
            (patientData.usesMarijuana === 'no' || patientData.marijuanaFrequency !== "") &&
            Object.values(errors).every(error => error === "");


        if (formIsValid) {
            try {

                const dataToSend = {
                    ...patientData,
                    usesMarijuana: patientData.usesMarijuana,
                    marijuanaFrequency: patientData.usesMarijuana === 'yes' ? patientData.marijuanaFrequency : null,
                    subjectName: patientData.subjectName.trim(),
                    birthYear: Number(patientData.birthYear),
                    height: Number(patientData.height),
                    heightUnit: patientData.heightUnit,
                    weight: Number(patientData.weight),
                    bmi: Number(patientData.bmi),
                    isSmoker: patientData.isSmoker,
                    cigarettesPerDay: patientData.isSmoker === 'yes' ? Number(patientData.cigarettesPerDay) : null,
                    alcoholConsumption: patientData.alcoholConsumption,
                    exerciseIntensity: patientData.exerciseIntensity,
                    hasThyroidCondition: patientData.hasThyroidCondition,
                    hasHypertension: patientData.hasHypertension,
                    parentalHypertensionHistory: patientData.parentalHypertensionHistory,
                    bodyTemperature: patientData.bodyTemperature ? parseFloat(patientData.bodyTemperature) : null,
                    bodyTemperatureUnit: patientData.bodyTemperatureUnit,
                    hasConcomitantMedication: patientData.hasConcomitantMedication,
                    concomitantMedication: patientData.hasConcomitantMedication === 'yes' ? patientData.concomitantMedication.map(med => ({
                        ...med,
                        startDate: med.startDate ? new Date(med.startDate).getTime()  : null,
                        endDate: med.ongoing ? null : (med.endDate ? new Date(med.endDate).getTime() : null)
                    })) : [],
                    hasMedicalHistory: patientData.hasMedicalHistory,
                    medicalHistory: patientData.hasMedicalHistory === 'yes' ? patientData.medicalHistory.map(entry => ({
                        ...entry,
                        startDate: entry.startDate ? new Date(entry.startDate).getTime() : null,
                        endDate: entry.ongoing ? null : (entry.endDate ? new Date(entry.endDate).getTime() : null)
                    }))
                        : []

                };
                const response = await axios.post(`${BASE_URL}web/createSubject`, dataToSend);
                if (response.status === 200) {
                    onPatientAdded();
                    resetForm();
                    toggle();
                } else {
                    (response.data.errorString)
                }
            } catch (error) {
                const str = "Error adding new patient: " + error.response.data.message
                alert(str)
            } finally {
                setIsSubmitting(false);
            }
        } else {
            console.log("Errors: ", errors)
            console.log("Form is invalid. Please check all fields.");
            setIsSubmitting(false);
        }
    };

    const validateField = (fieldName, value = patientData[fieldName]) => {
        let error = '';

        if (touched[fieldName] && (value === "" || value === null)) {
            if (fieldName === 'hasMedicalHistory') {
                error = 'Please select Yes or No';
            } else if (
                (fieldName === 'heightFeet' && patientData.heightUnit === 'ft_in') ||
                (fieldName === 'heightInches' && patientData.heightUnit === 'ft_in') ||
                (fieldName === 'childBearingPotential' && patientData.gender === 'female') ||
                (fieldName === 'childBearingPotentialReason' && patientData.childBearingPotential === 'notAWomanOfChildBearingPotential') ||
                (fieldName === 'childBearingPotentialOther' && patientData.childBearingPotentialReason === 'other') ||
                (fieldName === 'otherRace' && patientData.race === 'other')

            ) {
                error = 'This field is required';
            }
        } else {
            switch (fieldName) {
                case 'usesMarijuana':
                case 'marijuanaFrequency':
                    if (value === '') {
                        error = 'This field is required';
                    }
                    break;
                case 'bodyTemperature':
                    if (value !== "") {
                        const temp = parseFloat(value);
                        const min = patientData.bodyTemperatureUnit === "F" ? 75 : 30;
                        const max = patientData.bodyTemperatureUnit === "F" ? 115 : 44;
                        if (isNaN(temp) || temp < min || temp > max) {
                            error = `Temperature must be between ${min} and ${max} ${patientData.bodyTemperatureUnit}`;
                        }
                    }
                    break;
                case 'cigarettesPerDay':
                    if (patientData.isSmoker === 'yes' && (value === '' || isNaN(value) || parseInt(value) < 0)) {
                        error = 'Please enter a valid non-negative number of cigarettes';
                    }
                    break;
                case 'isSmoker':
                case 'alcoholConsumption':
                case 'exerciseIntensity':
                case 'hasThyroidCondition':
                case 'hasHypertension':
                case 'parentalHypertensionHistory':
                    if (value === '') {
                        error = 'This field is required';
                    }
                    break;
                case 'subjectName':
                    if (value && value.trim().length === 0) {
                        error = 'Name cannot be only spaces';
                    } else if (value && !/^[a-zA-Z0-9]+(?: [a-zA-Z0-9]+)*$/.test(value)) {
                        error = 'Name should only contain letters, numbers, and single spaces between words';
                    }
                    break;
                case 'birthYear':
                    const currentYear = new Date().getFullYear();
                    if (value < 1900 || value > currentYear - 17) {
                        error = `Birth year must be between 1900 and ${currentYear - 17}`;
                    }
                    break;
                case 'height':
                    if (patientData.heightUnit === 'cm' && (value < 50 || value > 300)) {
                        error = "Height must be between 50 cm and 300 cm";
                    }
                    break;
                case 'heightFeet':
                    if (patientData.heightUnit === 'ft_in' && (value < 3 || value > 8)) {
                        error = "Height in feet must be between 3 and 8";
                    }
                    break;
                case 'heightInches':
                    if (patientData.heightUnit === 'ft_in' && (value < 0 || value >= 12)) {
                        error = "Height in inches must be between 0 and 11";
                    }
                    break;
                case 'weight':
                    if (patientData.weightUnit === 'kg' && (value < 30 || value >= 250)) {
                        error = "Weight in kg must be between 30 and 250";
                    } else if (patientData.weightUnit === 'lbs' && (value < 60 || value >= 500)) {
                        error = "Weight in lbs must be between 60 and 500";
                    }
                    break;
                case 'childBearingPotentialOther':
                    if (patientData.childBearingPotentialReason === 'other' && (!value || value.trim() === '')) {
                        error = 'Please specify the reason for not being a woman of childbearing potential';
                    }
                    break;
                case 'hasMedicalHistory':
                case 'hasConcomitantMedication':
                    if (value === null) {
                        error = 'This field is required';
                    }
                    break;

                default:
                    break;
            }
        }
        if (error) {
            console.log("New Error:", fieldName, error)
            console.log("Patient Data:", patientData)
        }


        setErrors(prevErrors => ({
            ...prevErrors,
            [fieldName]: error
        }));
    };

    const handleCancel = () => {
        resetForm();
        toggle();
    };

    const handleMedicalHistoryChange = (index, field, value) => {
        const updatedHistory = [...patientData.medicalHistory];
        updatedHistory[index] = { ...updatedHistory[index], [field]: value };
        if (field === 'ongoing') {
            updatedHistory[index] = {
                ...updatedHistory[index],
                ongoing: value,
                endDate: value ? '' : updatedHistory[index].endDate // Clear end date if ongoing is checked
            };
        }
        if (field === 'startDate' || field === 'endDate') {
            const startDate = field === 'startDate' ? value : updatedHistory[index].startDate;
            const endDate = field === 'endDate' ? value : updatedHistory[index].endDate;

            if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
                // Set an error state for this specific entry
                setErrors(prev => ({
                    ...prev,
                    [`medicalHistory${index}DateError`]: 'End date cannot be before start date'
                }));
            } else {
                // Clear the error if dates are valid
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`medicalHistory${index}DateError`];
                    return newErrors;
                });
            }
        }

        setPatientData({ ...patientData, medicalHistory: updatedHistory });
    };
    //useEffect(() => {
    //    console.log('Patient Data:', patientData);
    //}, [patientData]);

    const addMedicalHistoryEntry = () => {
        setPatientData({
            ...patientData,
            medicalHistory: [
                ...patientData.medicalHistory,
                { condition: '', startDate: '', endDate: '', ongoing: false }
            ]
        });
    };

    const removeMedicalHistoryEntry = (index) => {
        setPatientData(prevData => {
            const updatedHistory = prevData.medicalHistory.filter((_, i) => i !== index);
            return { ...prevData, medicalHistory: updatedHistory };
        });
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`medicalHistory${index}DateError`];
            return newErrors;
        });
    };

    const handleConcomitantMedicationChange = (index, field, value) => {
        const updatedMedication = [...patientData.concomitantMedication];
        if (field === 'medication') {
            // Handle medication selection/input
            updatedMedication[index] = {
                ...updatedMedication[index],
                medication: value.toLowerCase()
            };
        } else if (field === 'ongoing') {
            updatedMedication[index] = {
                ...updatedMedication[index],
                ongoing: value,
                endDate: value ? '' : updatedMedication[index].endDate
            };
        } else {
            updatedMedication[index] = { ...updatedMedication[index], [field]: value };
        }

        // Perform date validation when the field loses focus
        if (field === 'startDate' || field === 'endDate') {
            const startDate = field === 'startDate' ? value : updatedMedication[index].startDate;
            const endDate = field === 'endDate' ? value : updatedMedication[index].endDate;
            const thirtyDaysAgo = getThirtyDaysAgo();

            let dateError = '';

            if (startDate && endDate) {
                if (new Date(startDate) > new Date(endDate)) {
                    dateError = 'End date cannot be before start date';
                } else if (new Date(endDate) < new Date(thirtyDaysAgo)) {
                    dateError = 'End date must be within the last 30 days';
                }
            }

            if (dateError) {
                setErrors(prev => ({
                    ...prev,
                    [`concomitantMedication${index}DateError`]: dateError
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`concomitantMedication${index}DateError`];
                    return newErrors;
                });
            }
        }

        setPatientData({ ...patientData, concomitantMedication: updatedMedication });
    };

    const addConcomitantMedication = () => {
        setPatientData({
            ...patientData,
            concomitantMedication: [
                ...patientData.concomitantMedication,
                { medication: '', indication: '', dose: '', doseUnit: '', route: '', frequency: '', startDate: '', endDate: '', ongoing: false, takenForAE: false }
            ]
        });
    };

    const removeConcomitantMedication = (index) => {
        setPatientData(prevData => {
            const updatedMedication = prevData.concomitantMedication.filter((_, i) => i !== index);
            return { ...prevData, concomitantMedication: updatedMedication };
        });
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`concomitantMedication${index}DateError`];
            return newErrors;
        });
    };

    const renderConcomitantMedicationForm = () => (
        <>
            <FormGroup>
                <Label>
                    Prior (within 30 days of screening) and current medications should be entered *
                    End date must be within the last 30 days or marked as ongoing.
                </Label>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="hasConcomitantMedication"
                            value="yes"
                            checked={patientData.hasConcomitantMedication === 'yes'}
                            onClick={handleInputChange}
                            onBlur={() => setTouched(prev => ({ ...prev, hasConcomitantMedication: true }))}
                            invalid={touched.hasConcomitantMedication && !!errors.hasConcomitantMedication}
                        />
                        {' '}Yes
                    </Label>
                </FormGroup>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="hasConcomitantMedication"
                            value="no"
                            checked={patientData.hasConcomitantMedication === 'no'}
                            onClick={handleInputChange}
                            onBlur={() => setTouched(prev => ({ ...prev, hasConcomitantMedication: true }))}
                            invalid={touched.hasConcomitantMedication && !!errors.hasConcomitantMedication}
                        />
                        {' '}No
                    </Label>
                </FormGroup>
                {patientData.hasConcomitantMedication === null && (
                    <FormFeedback style={{ display: 'block' }}>Please answer the concomitant medication</FormFeedback>
                )}
            </FormGroup>

            {patientData.hasConcomitantMedication === 'yes' && (
                <>
                    {patientData.concomitantMedication.map((med, index) => (
                        <Row key={index}>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Medication (Generic)</Label>
                                    <Select
                                        required
                                        value={med.medication ? { value: med.medication, label: med.medication } : null}
                                        onChange={(option) => handleConcomitantMedicationChange(index, 'medication', option ? option.value : '')}
                                        options={MEDICATIONS}
                                        isClearable
                                        isSearchable
                                        placeholder="Search or enter..."
                                        allowCreateWhileLoading={true}
                                        createOptionPosition="first"
                                        formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                                        noOptionsMessage={() => "Type to search or enter custom"}
                                        onInputChange={(inputValue, { action }) => {
                                            if (action === 'input-change' && inputValue) {
                                                const matchedMed = MEDICATIONS.find(med =>
                                                    med.value.toLowerCase() === inputValue.toLowerCase()
                                                );
                                                if (!matchedMed) {
                                                    handleConcomitantMedicationChange(index, 'medication', inputValue);
                                                }
                                            }
                                        }}
                                        isValidNewOption={(inputValue) => inputValue.length > 0}
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '38px'
                                            }),
                                            menu: (base) => ({
                                                ...base,
                                                zIndex: 9999
                                            })
                                        }}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Indication</Label>
                                    <Input
                                        type="text"
                                        value={med.indication}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'indication', e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <FormGroup>
                                    <Label>Dose</Label>
                                    <Input
                                        type="number"
                                        value={med.dose}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'dose', e.target.value)}
                                        onWheel={(e) => e.target.blur()}
                                        step="1"
                                        min={0}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <FormGroup>
                                    <Label>Dose Unit</Label>
                                    <Input
                                        type="text"
                                        value={med.doseUnit}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'doseUnit', e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <FormGroup>
                                    <Label>Route</Label>
                                    <Input
                                        type="text"
                                        value={med.route}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'route', e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <FormGroup>
                                    <Label>Frequency</Label>
                                    <Input
                                        type="text"
                                        value={med.frequency}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'frequency', e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        value={med.startDate}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'startDate', e.target.value)}
                                        max={getTodayDate()}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        value={med.endDate}
                                        onChange={(e) => handleConcomitantMedicationChange(index, 'endDate', e.target.value)}
                                        disabled={med.ongoing}
                                        min={getThirtyDaysAgo()}
                                        max={getTodayDate()}
                                        invalid={!!errors[`concomitantMedication${index}DateError`]}

                                    />
                                    <FormFeedback>{errors[`concomitantMedication${index}DateError`]}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            checked={med.ongoing}
                                            onClick={(e) => handleConcomitantMedicationChange(index, 'ongoing', !med.ongoing)}
                                        /> Ongoing
                                    </Label>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            checked={med.takenForAE}
                                            onClick={(e) => handleConcomitantMedicationChange(index, 'takenForAE', !med.takenForAE)}
                                        /> Taken for Adverse Event?
                                    </Label>
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <Button color="danger" size="sm" onClick={() => removeConcomitantMedication(index)}>
                                    Remove
                                </Button>
                            </Col>
                            <Col md={12}>
                                {!med.endDate && !med.ongoing && (
                                    <FormFeedback style={{ display: 'block' }}>
                                        This medication must have an end date or be marked as ongoing.
                                    </FormFeedback>
                                )}
                            </Col>
                        </Row>
                    ))}
                    {patientData.concomitantMedication.length >= 0 && (
                        <Button color="primary" onClick={addConcomitantMedication}>
                            Add Medication
                        </Button>
                    )}

                    {patientData.concomitantMedication.length === 0 && (
                        <FormFeedback style={{ display: 'block' }}>Please add at least one concomitant medication entry</FormFeedback>
                    )}
                </>
            )}
        </>
    );

    const renderMedicalHistoryForm = () => (

        <>
            <FormGroup>
                <Label>
                    Does subject have any clinically significant medical/surgical history? *
                    Include medical conditions, allergies, surgical history, and reason for ICU hospitalization.
                </Label>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="hasMedicalHistory"
                            value="yes"
                            checked={patientData.hasMedicalHistory === 'yes'}
                            onClick={handleInputChange}
                            onBlur={() => setTouched(prev => ({ ...prev, hasMedicalHistory: true }))}
                            invalid={touched.hasMedicalHistory && !!errors.hasMedicalHistory}
                        />
                        {' '}Yes
                    </Label>
                </FormGroup>
                <FormGroup check>
                    <Label check>
                        <Input
                            type="radio"
                            name="hasMedicalHistory"
                            value="no"
                            checked={patientData.hasMedicalHistory === 'no'}
                            onClick={handleInputChange}
                            onBlur={() => setTouched(prev => ({ ...prev, hasMedicalHistory: true }))}
                            invalid={touched.hasMedicalHistory && !!errors.hasMedicalHistory}
                        />
                        {' '}No
                    </Label>
                </FormGroup>
                {patientData.hasMedicalHistory === null && (
                    <FormFeedback style={{ display: 'block' }}>Please answer the medical history</FormFeedback>
                )}
            </FormGroup>

            {patientData.hasMedicalHistory === 'yes' && (
                <>
                    {patientData.medicalHistory.map((entry, index) => (
                        <Row key={index}>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Condition</Label>
                                    <Input
                                        type="text"
                                        value={entry.condition}
                                        onChange={(e) => handleMedicalHistoryChange(index, 'condition', e.target.value)}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>Start Date</Label>
                                    <Input
                                        type="date"
                                        value={entry.startDate}
                                        onChange={(e) => handleMedicalHistoryChange(index, 'startDate', e.target.value)}
                                        max={getTodayDate()}
                                        required
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup>
                                    <Label>End Date</Label>
                                    <Input
                                        type="date"
                                        value={entry.endDate}
                                        onChange={(e) => handleMedicalHistoryChange(index, 'endDate', e.target.value)}
                                        disabled={entry.ongoing}
                                        max={getTodayDate()}
                                        invalid={!!errors[`medicalHistory${index}DateError`]}
                                    />
                                    <FormFeedback>{errors[`medicalHistory${index}DateError`]}</FormFeedback>
                                </FormGroup>
                            </Col>
                            <Col md={2}>
                                <FormGroup check>
                                    <Label check>
                                        <Input
                                            type="checkbox"
                                            checked={entry.ongoing}
                                            onClick={(e) => {
                                                handleMedicalHistoryChange(index, 'ongoing', !entry.ongoing);
                                            }}
                                        /> Ongoing
                                    </Label>
                                </FormGroup>
                            </Col>
                            <Col md={1}>
                                <Button color="danger" size="sm" onClick={() => removeMedicalHistoryEntry(index)}>
                                    Remove
                                </Button>
                            </Col>
                            <Col md={12}>
                                {!entry.endDate && !entry.ongoing && (
                                    <FormFeedback style={{ display: 'block' }}>
                                        This entry must have an end date or be marked as ongoing.
                                    </FormFeedback>
                                )}
                            </Col>
                        </Row>
                    ))}

                    {patientData.medicalHistory.length >= 0 && (
                        <Button color="primary" onClick={addMedicalHistoryEntry}>
                            Add Entry
                        </Button>
                    )}
                    {patientData.medicalHistory.length === 0 && (
                        <FormFeedback style={{ display: 'block' }}>Please add at least one medical history entry</FormFeedback>
                    )}

                </>
            )}
        </>
    );

    const renderFormGroup = (label, name, type = "text", options = null) => (
        <FormGroup>
            <Label for={name}>{label} *</Label>
            {type === "select" ? (
                <Input
                    type={type}
                    name={name}
                    id={name}
                    value={patientData[name] || ""}
                    onChange={handleInputChange}
                    onBlur={() => setTouched(prev => ({ ...prev, [name]: true }))}
                    invalid={touched[name] && !!errors[name]}
                    required
                >
                    <option value="">Select {label.toLowerCase()}</option>
                    {options}
                </Input>
            ) : (
                <Input
                    type={type}
                    name={name}
                    id={name}
                    placeholder={`Enter ${label.toLowerCase()}`}
                    value={patientData[name]}
                    onChange={handleInputChange}
                    onBlur={() => setTouched(prev => ({ ...prev, [name]: true }))}
                    invalid={touched[name] && !!errors[name]}
                    required
                    onWheel={(e) => e.target.blur()} // Prevent mouse wheel scrolling
                    onKeyDown={(e) => {
                        if (type === "number" && e.key === ".") {
                            e.preventDefault(); // Prevent decimal point and 'e' for number inputs
                        }
                    }}
                    step="1"
                />
            )}
            <FormFeedback>{errors[name]}</FormFeedback>
        </FormGroup>
    );

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size="xl" backdrop="static" keyboard={false}>
            <ModalHeader toggle={handleCancel}>Add New Patient</ModalHeader>

            <Form onSubmit={handleAddProfile}>
                <ModalBody>
                    <h4>Basic Information</h4>
                    <hr />
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="subjectName">Patient Name *</Label>
                                <Input
                                    type="text"
                                    name="subjectName"
                                    id="subjectName"
                                    placeholder="Enter patient name"
                                    value={patientData.subjectName || ''}
                                    onChange={handleInputChange}
                                    onBlur={() => setTouched(prev => ({ ...prev, subjectName: true }))}
                                    invalid={touched.subjectName && !!errors.subjectName}
                                    required
                                />
                                <FormFeedback>{errors.subjectName}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            {renderFormGroup("Birth Year", "birthYear", "number")}
                        </Col>
                    </Row>
                    {calculatedAge !== null && (
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label>Calculated Age: {calculatedAge} years</Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col md={6}>
                            {renderFormGroup("Gender at birth", "gender", "select",
                                <>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="preferNotToSay">Prefer not to say</option>
                                </>
                            )}
                        </Col>
                    </Row>
                    <h4 className="mt-4">Physical Measurements</h4>
                    <hr />
                    <Row>
                        <Col md={6}>
                            {patientData.heightUnit === 'cm' ? (
                                renderFormGroup("Height (cm)", "height", "number")
                            ) : (
                                <>
                                    {renderFormGroup("Height (Feet)", "heightFeet", "number")}
                                    {renderFormGroup("Height (Inches)", "heightInches", "number")}
                                </>
                            )}
                        </Col>
                        <Col md={6}>
                            {renderFormGroup("Height Unit", "heightUnit", "select",
                                <>
                                    <option value="ft_in">ft/in</option>
                                    <option value="cm">cm</option>

                                </>
                            )}
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            {renderFormGroup("Weight", "weight", "number")}
                        </Col>
                        <Col md={6}>
                            {renderFormGroup("Weight Unit", "weightUnit", "select",
                                <>
                                    <option value="lbs">lbs</option>
                                    <option value="kg">kg</option>

                                </>
                            )}
                        </Col>
                    </Row>
                    {patientData.bmi !== null && (
                        <Row>
                            <Col>
                                <FormGroup>
                                    <Label>BMI : {patientData.bmi}  </Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    )}

                    <h4 className="mt-4">Additional Information</h4>
                    <hr />
                    <FormGroup>
                        <Label for="skinColor">
                            Skin Color (Fitzpatrick Scale) *
                            <span className="tooltip-container ml-2">
                                <span className="text-primary" style={{ cursor: 'pointer' }}>Scale Image</span>
                                <div className="tooltip-content">
                                    <img src={fitzpatrickScaleImage} alt="Fitzpatrick Scale" style={{ width: '100%' }} />
                                </div>
                            </span>
                        </Label>
                        <Input
                            type="select"
                            name="skinColor"
                            id="skinColor"
                            value={patientData.skinColor || ""}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select skin color</option>
                            <option value="TYPEI">Type I</option>
                            <option value="TYPEII">Type II</option>
                            <option value="TYPEIII">Type III</option>
                            <option value="TYPEIV">Type IV</option>
                            <option value="TYPEV">Type V</option>
                            <option value="TYPEVI">Type VI</option>
                        </Input>
                    </FormGroup>

                    {patientData.gender === 'female' && (
                        renderFormGroup("Child Bearing Potential", "childBearingPotential", "select",
                            <>
                                <option value="notAWomanOfChildBearingPotential">Not a Woman of Child Bearing Potential</option>
                                <option value="womanOfChildBearingPotential">Woman of Child Bearing Potential</option>
                            </>
                        )
                    )}
                    {patientData.childBearingPotential === "notAWomanOfChildBearingPotential" && (
                        renderFormGroup("Reason", "childBearingPotentialReason", "select",
                            <>
                                <option value="postmenopausalForTwoYears">Postmenopausal for 2 years</option>
                                <option value="surgicallySterile">Surgically sterile</option>
                                <option value="other">Other</option>
                            </>
                        )
                    )}
                    {patientData.childBearingPotentialReason === "other" && (
                        renderFormGroup("Specify Other Reason", "childBearingPotentialOther")
                    )}
                    {renderFormGroup("Race", "race", "select",
                        <>
                            <option value="americanIndianOrAlaskaNative">American Indian or Alaska Native</option>
                            <option value="nativeHawaiianOrOtherPacificIslander">Native Hawaiian or Other Pacific Islander</option>
                            <option value="asian">Asian</option>
                            <option value="white">White</option>
                            <option value="blackOrAfricanAmerican">Black/African American</option>
                            <option value="other">Other</option>
                        </>
                    )}
                    {patientData.race === "other" && (
                        renderFormGroup("Specify Other Race", "otherRace")
                    )}
                    {renderFormGroup("Ethnicity", "ethnicity", "select",
                        <>
                            <option value="hispanicOrLatino">Hispanic or Latino</option>
                            <option value="notHispanicOrLatino">Not Hispanic or Latino</option>
                        </>
                    )}
                    <h4 className="mt-4">Patient Status</h4>
                    <hr />
                    <FormGroup>
                        <Label>Does patient use marijuana? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="usesMarijuana"
                                    value="yes"
                                    checked={patientData.usesMarijuana === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="usesMarijuana"
                                    value="no"
                                    checked={patientData.usesMarijuana === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                    </FormGroup>

                    {patientData.usesMarijuana === "yes" && (
                        <FormGroup>
                            <Label for="marijuanaFrequency">How often does patient use marijuana? *</Label>
                            <Input
                                type="select"
                                name="marijuanaFrequency"
                                id="marijuanaFrequency"
                                value={patientData.marijuanaFrequency}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="">Select...</option>
                                <option value="Daily">Daily</option>
                                <option value="Weekly">Weekly</option>
                                <option value="Social">Social</option>
                            </Input>
                        </FormGroup>
                    )}
                    <FormGroup>
                        <Label>Does the patient have diabetes? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasDiabetes"
                                    value="yes"
                                    checked={patientData.hasDiabetes === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasDiabetes"
                                    value="no"
                                    checked={patientData.hasDiabetes === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                    </FormGroup>
                    <FormGroup>
                        <Label>Does patient smoke? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="isSmoker"
                                    value="yes"
                                    checked={patientData.isSmoker === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="isSmoker"
                                    value="no"
                                    checked={patientData.isSmoker === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                        <FormFeedback>{errors.isSmoker}</FormFeedback>
                    </FormGroup>

                    {patientData.isSmoker === "yes" && (
                        <FormGroup>
                            <Label for="cigarettesPerDay">How many cigarettes does patient smoke per day? *</Label>
                            <Input
                                type="number"
                                name="cigarettesPerDay"
                                id="cigarettesPerDay"
                                value={patientData.cigarettesPerDay || ''}
                                onChange={handleInputChange}
                                min="0"
                                max="500"
                                required
                            />
                            <FormFeedback>{errors.cigarettesPerDay}</FormFeedback>
                        </FormGroup>
                    )}

                    {/* Alcohol Consumption */}
                    <FormGroup>
                        <Label for="alcoholConsumption">How often does patient have any kind of drink containing alcohol? *</Label>
                        <Input
                            type="select"
                            name="alcoholConsumption"
                            id="alcoholConsumption"
                            value={patientData.alcoholConsumption}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select...</option>
                            <option value="Daily">Daily</option>
                            <option value="Weekly">Weekly</option>
                            <option value="Social">Social</option>
                            <option value="None">None</option>
                        </Input>
                    </FormGroup>

                    {/* Exercise Intensity */}
                    <FormGroup>
                        <Label for="exerciseIntensity">Describe patient's usual exercise intensity level? *</Label>
                        <Input
                            type="select"
                            name="exerciseIntensity"
                            id="exerciseIntensity"
                            value={patientData.exerciseIntensity}
                            onChange={handleInputChange}
                            required
                        >
                            <option value="">Select...</option>
                            <option value="Low">Low</option>
                            <option value="Moderate">Moderate</option>
                            <option value="High">High</option>
                        </Input>
                    </FormGroup>

                    {/* Thyroid Condition */}
                    <FormGroup>
                        <Label>Does Patient have any thyroid conditions? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasThyroidCondition"
                                    value="yes"
                                    checked={patientData.hasThyroidCondition === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasThyroidCondition"
                                    value="no"
                                    checked={patientData.hasThyroidCondition === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                    </FormGroup>

                    {/* Hypertension */}
                    <FormGroup>
                        <Label>Does Patient have Hypertension? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasHypertension"
                                    value="yes"
                                    checked={patientData.hasHypertension === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="hasHypertension"
                                    value="no"
                                    checked={patientData.hasHypertension === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                    </FormGroup>

                    {/* Parental Hypertension History */}
                    <FormGroup>
                        <Label>Have either of patient's parents experienced high blood pressure or hypertension? *</Label>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="parentalHypertensionHistory"
                                    value="yes"
                                    checked={patientData.parentalHypertensionHistory === "yes"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                Yes
                            </Label>
                        </FormGroup>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="radio"
                                    name="parentalHypertensionHistory"
                                    value="no"
                                    checked={patientData.parentalHypertensionHistory === "no"}
                                    onClick={handleInputChange}
                                    required
                                />{' '}
                                No
                            </Label>
                        </FormGroup>
                    </FormGroup>

                    {/* Body Temperature */}
                    {/* Body Temperature */}
                    {/* Body Temperature */}
                    <FormGroup>
                        <Label for="bodyTemperature">
                            Body Temperature *
                        </Label>

                        <Row>
                            <Col md={6}>
                                <Input
                                    type="number"
                                    name="bodyTemperature"
                                    id="bodyTemperature"
                                    value={patientData.bodyTemperature}
                                    onChange={handleInputChange}
                                    min={patientData.bodyTemperatureUnit === "F" ? "75" : "30"}
                                    max={patientData.bodyTemperatureUnit === "F" ? "115" : "44"}
                                    step="0.1"
                                    required
                                />
                                <FormFeedback>{errors.bodyTemperature}</FormFeedback>
                            </Col>
                            <Col md={6}>
                                <Input
                                    type="select"
                                    name="bodyTemperatureUnit"
                                    id="bodyTemperatureUnit"
                                    value={patientData.bodyTemperatureUnit}
                                    onChange={handleInputChange}
                                >
                                    <option value="F">°F</option>
                                    <option value="C">°C</option>
                                </Input>
                            </Col>
                        </Row>
                    </FormGroup>
                    {(!patientData.bodyTemperature ||
                        (patientData.bodyTemperatureUnit === "F" && (parseFloat(patientData.bodyTemperature) < 75 || parseFloat(patientData.bodyTemperature) > 115)) ||
                        (patientData.bodyTemperatureUnit === "C" && (parseFloat(patientData.bodyTemperature) < 30 || parseFloat(patientData.bodyTemperature) > 44))) && (
                            <div style={{ color: 'red', fontSize: '80%', marginTop: '-10px', marginBottom: '10px' }}>
                                Temperature must be between {patientData.bodyTemperatureUnit === "F" ? "75-115°F" : "30-44°C"}
                            </div>
                        )}

                    <h4 className="mt-4">Medical History</h4>
                    <hr />
                    <Row>
                        <Col>
                            {renderMedicalHistoryForm()}
                        </Col>
                    </Row>
                    <h4 className="mt-4">Concomitant Medication</h4>
                    <hr />
                    <Row>
                        <Col>
                            {renderConcomitantMedicationForm()}
                        </Col>
                    </Row>
                    <Row>
                        <Col>

                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>

                    <Button color="secondary" onClick={handleCancel} disabled={isSubmitting}>Cancel</Button>
                    <Button color="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Adding Patient...' : 'Add Patient'}
                    </Button>


                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default AddPatientModal;